import {
  Component,
  Input,
  TemplateRef,
  Type,
  ViewChild,
  AfterViewInit,
  HostListener,
  ElementRef,
  ComponentFactoryResolver,
  ViewContainerRef,
  ComponentRef,
  Output,
  EventEmitter,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { NgbAccordionDirective } from '@ng-bootstrap/ng-bootstrap';
import {
  FILTER_BUTTON_SELECTORS,
  RESET_BUTTON_SELECTORS,
  BUTTON_KEYWORDS,
  EVENT_CONFIG,
  FLOATING_ICON_CONFIG,
  SELECTOR_CONFIDENCE_MAP,
  TYPE_DETECTION_CONFIG,
  KEYWORD_DETECTION_CONFIG,
  DYNAMIC_COMPONENT_EVENTS,
  DYNAMIC_COMPONENT_METHODS,
  CSS_CLASSES,
  HTML_ATTRIBUTES,
  RESPONSIVE_CONFIG,
  DOM_EVENTS,
  BUTTON_TYPES,
  DETECTION_METHODS,
  ACCORDION_CONFIG,
  FUNCTION_TYPES,
} from '../../../../core/helpers/ui/toogle-panel.constant';
import {
  FilterButtonType,
  ButtonDetectionResult,
  EventConfig,
  MethodConfig,
  ButtonCategorization,
  DynamicComponentInstance,
  BoundEventHandlers,
  CleanupTasksConfig,
} from '../../../../core/interfaces/ui/toogle-panel.interface';

@Component({
  selector: 'shared-accordions-toogle-panel',
  templateUrl: './toogle-panel.component.html',
  styleUrls: ['./toogle-panel.component.scss'],
})
export class TooglePanelComponent
  implements OnInit, AfterViewInit, OnDestroy, BoundEventHandlers
{
  @Input() public isCollapsed: boolean = false;
  @Input() public title: string = 'words.filter';
  @Input() public headerTemplate: TemplateRef<HTMLElement> | null = null;
  @Input() public contentTemplate: TemplateRef<HTMLElement> | null = null;
  @Input() public contentComponent: Type<DynamicComponentInstance> | null =
    null;
  @Input() public autoCloseOnFilter: boolean = true;

  @Output() public filterApplied = new EventEmitter<any>();
  @Output() public filterReset = new EventEmitter<void>();
  @Output() public offcanvasOpened = new EventEmitter<void>();
  @Output() public offcanvasClosed = new EventEmitter<void>();

  @ViewChild('accordion') accordion?: NgbAccordionDirective;
  @ViewChild('dynamicComponentContainer', { read: ViewContainerRef })
  dynamicComponentContainer?: ViewContainerRef;

  private dragStartY: number = 0;
  private dragStartPosition: number = 0;

  public isDragging: boolean = false;
  private hasDragged: boolean = false;
  public isMobileView: boolean = false;
  public showOffcanvas: boolean = false;
  public showMobileButton: boolean = false;
  public floatingIconPosition: number = FLOATING_ICON_CONFIG.DEFAULT_POSITION;

  private domObserver?: MutationObserver;
  private dynamicComponentRef?: ComponentRef<DynamicComponentInstance>;

  public boundMouseMove?: (event: MouseEvent) => void;
  public boundMouseUp?: (event: MouseEvent) => void;
  public boundTouchMove?: (event: TouchEvent) => void;
  public boundTouchEnd?: (event: TouchEvent) => void;

  constructor(
    private elementRef: ElementRef,
    private componentFactoryResolver: ComponentFactoryResolver,
  ) {}

  ngOnInit(): void {
    this.checkScreenSize();
    this.loadSavedIconPosition();
  }

  @HostListener('window:resize', ['$event'])
  onResize(): void {
    this.checkScreenSize();
  }

  @HostListener('window:orientationchange', ['$event'])
  onOrientationChange(): void {
    setTimeout(() => {
      this.checkScreenSize();
    }, 100);
  }

  @HostListener('window:visualViewport.resize', ['$event'])
  onVisualViewportResize(): void {
    setTimeout(() => {
      this.checkScreenSize();
    }, 50);
  }

  private checkScreenSize(): void {
    const wasMobile = this.isMobileView;
    this.isMobileView = window.innerWidth < RESPONSIVE_CONFIG.MOBILE_BREAKPOINT;

    if (wasMobile && !this.isMobileView && this.showOffcanvas) {
      this.closeOffcanvas();
    }

    this.showMobileButton = this.isMobileView;

    if (this.isMobileView) {
      this.validateFloatingIconPosition();
    }
  }

  private validateFloatingIconPosition(): void {
    const viewportHeight = window.innerHeight;
    const iconHeight = 60;
    const safeMargin = 10;

    const minSafePercent =
      ((safeMargin + iconHeight / 2) / viewportHeight) * 100;
    const maxSafePercent =
      ((viewportHeight - safeMargin - iconHeight / 2) / viewportHeight) * 100;

    if (
      this.floatingIconPosition < minSafePercent ||
      this.floatingIconPosition > maxSafePercent
    ) {
      this.floatingIconPosition = Math.max(
        minSafePercent,
        Math.min(maxSafePercent, this.floatingIconPosition),
      );

      if (typeof localStorage !== 'undefined') {
        localStorage.setItem(
          FLOATING_ICON_CONFIG.STORAGE_KEY,
          this.floatingIconPosition.toString(),
        );
      }
    }
  }

  ngAfterViewInit(): void {
    this.initializeAccordion();
    this.scheduleAutoFilterDetection();
    this.initializeDynamicComponent();
  }

  private initializeAccordion(): void {
    if (!this.accordion || this.isMobileView) return;

    if (this.isCollapsed) {
      this.accordion.collapse(ACCORDION_CONFIG.FIRST_PANEL);
    } else {
      this.accordion.expand(ACCORDION_CONFIG.FIRST_PANEL);
    }
  }

  private scheduleAutoFilterDetection(): void {
    setTimeout(() => {
      this.setupAutoFilterDetection();
    }, EVENT_CONFIG.SETUP_AUTO_DETECTION_DELAY);
  }

  private initializeDynamicComponent(): void {
    if (this.contentComponent && this.dynamicComponentContainer) {
      const componentFactory =
        this.componentFactoryResolver.resolveComponentFactory(
          this.contentComponent,
        );
      this.dynamicComponentRef =
        this.dynamicComponentContainer.createComponent(componentFactory);

      this.subscribeToDynamicComponentEvents();
    }
  }

  public collapseAccordion(): void {
    if (this.isMobileView) {
      this.toggleOffcanvas();
      return;
    }

    if (!this.accordion) return;

    this.accordion.toggle(ACCORDION_CONFIG.FIRST_PANEL);
    this.isCollapsed = !this.isCollapsed;
  }

  private toggleOffcanvas(): void {
    if (this.showOffcanvas) {
      this.closeOffcanvas();
    } else {
      this.openOffcanvas();
    }
  }

  public openOffcanvas(): void {
    this.showOffcanvas = true;
    document.body.classList.add(CSS_CLASSES.OVERFLOW_HIDDEN);
    this.offcanvasOpened.emit();
  }

  public closeOffcanvas(): void {
    this.showOffcanvas = false;
    document.body.classList.remove(CSS_CLASSES.OVERFLOW_HIDDEN);
    this.offcanvasClosed.emit();
  }

  public closeOffcanvasOnAction(): void {
    if (this.isMobileView && this.showOffcanvas && this.autoCloseOnFilter) {
      this.closeOffcanvas();
    }
  }

  public onFilterApplied(filterData?: any): void {
    this.filterApplied.emit(filterData);
    this.closeOffcanvasOnAction();
  }

  public onFilterReset(): void {
    this.filterReset.emit();
    this.closeOffcanvasOnAction();
  }

  private subscribeToDynamicComponentEvents(): void {
    const instance = this.dynamicComponentRef?.instance;
    if (!instance) return;

    this.subscribeToEventEmitters(instance);
    this.interceptMethodCalls(instance);
  }

  private subscribeToEventEmitters(instance: DynamicComponentInstance): void {
    const eventConfig: EventConfig[] = DYNAMIC_COMPONENT_EVENTS.map(
      ({ name, type }) => ({
        name,
        handler:
          type === BUTTON_TYPES.FILTER
            ? (data: any) => this.onFilterApplied(data)
            : () => this.onFilterReset(),
      }),
    );

    eventConfig.forEach(({ name, handler }) => {
      const eventEmitter = instance[name];
      eventEmitter?.subscribe?.(handler);
    });
  }

  private interceptMethodCalls(instance: DynamicComponentInstance): void {
    const methodConfig: MethodConfig[] = DYNAMIC_COMPONENT_METHODS.map(
      ({ name, type }) => ({
        name,
        handler:
          type === BUTTON_TYPES.FILTER
            ? (args: any[]) => this.onFilterApplied(args[0])
            : () => this.onFilterReset(),
      }),
    );

    methodConfig.forEach(({ name, handler }) => {
      this.wrapMethod(instance, name, handler);
    });
  }

  private wrapMethod(
    instance: DynamicComponentInstance,
    methodName: string,
    callback: (args: any[]) => void,
  ): void {
    const originalMethod = instance[methodName];

    if (typeof originalMethod !== FUNCTION_TYPES.FUNCTION) return;

    instance[methodName] = (...args: any[]) => {
      const result = originalMethod.apply(instance, args);
      callback(args);
      return result;
    };
  }

  private setupAutoFilterDetection(): void {
    const container = this.elementRef.nativeElement;

    this.detectAndBindFilterButtons(container);

    this.setupDOMObserver(container);
  }

  private detectAndBindFilterButtons(container: HTMLElement): void {
    const { filterButtons, resetButtons } = this.categorizeButtons(container);

    filterButtons.forEach((result) =>
      this.bindButtonEvent(result.element, BUTTON_TYPES.FILTER, () =>
        this.onFilterApplied(),
      ),
    );

    resetButtons.forEach((result) =>
      this.bindButtonEvent(result.element, BUTTON_TYPES.RESET, () =>
        this.onFilterReset(),
      ),
    );

    this.bindFormEvents(container);
  }

  private categorizeButtons(container: HTMLElement): ButtonCategorization {
    const allButtons = this.getAllButtons(container);
    const filterButtons: ButtonDetectionResult[] = [];
    const resetButtons: ButtonDetectionResult[] = [];

    allButtons.forEach((button) => {
      const detection = this.detectButtonType(button);
      detection.type === BUTTON_TYPES.FILTER
        ? filterButtons.push(detection)
        : resetButtons.push(detection);
    });

    return { filterButtons, resetButtons };
  }

  private getAllButtons(container: HTMLElement): HTMLElement[] {
    const selectorGroups = [
      Object.values(FILTER_BUTTON_SELECTORS),
      Object.values(RESET_BUTTON_SELECTORS),
    ];

    const allSelectors = selectorGroups.flat().join(', ');
    return Array.from(
      container.querySelectorAll(allSelectors),
    ) as HTMLElement[];
  }

  private detectButtonType(button: HTMLElement): ButtonDetectionResult {
    const methods = {
      type: () => this.detectByType(button),
      selector: () => this.detectBySelector(button),
      keyword: () => this.detectByKeyword(button),
    };

    const typeResult =
      methods.type() || methods.selector() || methods.keyword();

    return (
      typeResult ?? {
        element: button,
        type: BUTTON_TYPES.FILTER,
        method: DETECTION_METHODS.SELECTOR,
        confidence: 0.1,
      }
    );
  }

  private detectByType(button: HTMLElement): ButtonDetectionResult | null {
    const buttonType = button.getAttribute(HTML_ATTRIBUTES.TYPE)?.toLowerCase();

    return buttonType === BUTTON_TYPES.RESET
      ? {
          element: button,
          type: TYPE_DETECTION_CONFIG.RESET.type,
          method: TYPE_DETECTION_CONFIG.RESET.method,
          confidence: TYPE_DETECTION_CONFIG.RESET.confidence,
        }
      : buttonType === BUTTON_TYPES.SUBMIT
        ? {
            element: button,
            type: TYPE_DETECTION_CONFIG.SUBMIT.type,
            method: TYPE_DETECTION_CONFIG.SUBMIT.method,
            confidence: TYPE_DETECTION_CONFIG.SUBMIT.confidence,
          }
        : null;
  }

  private detectBySelector(button: HTMLElement): ButtonDetectionResult | null {
    const matchedEntry = Array.from(SELECTOR_CONFIDENCE_MAP.entries()).find(
      ([selector]) => button.matches(selector),
    );

    if (!matchedEntry) return null;

    const [, config] = matchedEntry;
    return {
      element: button,
      type: config.type,
      method: DETECTION_METHODS.SELECTOR,
      confidence: config.confidence,
    };
  }

  private detectByKeyword(button: HTMLElement): ButtonDetectionResult | null {
    const extractedText = this.extractButtonText(button);
    const keywordMap = {
      reset: BUTTON_KEYWORDS.RESET,
      apply: BUTTON_KEYWORDS.APPLY,
    };

    const hasResetKeywords = keywordMap.reset.some((keyword: string) =>
      extractedText.some((text: string) => text.includes(keyword)),
    );

    const hasApplyKeywords = keywordMap.apply.some((keyword: string) =>
      extractedText.some((text: string) => text.includes(keyword)),
    );

    return hasResetKeywords && !hasApplyKeywords
      ? {
          element: button,
          type: BUTTON_TYPES.RESET,
          method: KEYWORD_DETECTION_CONFIG.METHOD,
          confidence: KEYWORD_DETECTION_CONFIG.CONFIDENCE,
        }
      : hasApplyKeywords && !hasResetKeywords
        ? {
            element: button,
            type: BUTTON_TYPES.FILTER,
            method: KEYWORD_DETECTION_CONFIG.METHOD,
            confidence: KEYWORD_DETECTION_CONFIG.CONFIDENCE,
          }
        : null;
  }

  private extractButtonText(button: HTMLElement): string[] {
    return [
      button.textContent?.toLowerCase() || '',
      button.className.toLowerCase(),
      button.getAttribute(HTML_ATTRIBUTES.DATA_ACTION)?.toLowerCase() || '',
      button.getAttribute(HTML_ATTRIBUTES.DATA_FILTER_ACTION)?.toLowerCase() ||
        '',
    ];
  }

  private bindButtonEvent(
    button: HTMLElement,
    type: FilterButtonType,
    callback: () => void,
  ): void {
    if (button.hasAttribute(CSS_CLASSES.AUTO_CLOSE_BOUND)) return;

    button.addEventListener(DOM_EVENTS.CLICK, () => {
      setTimeout(callback, EVENT_CONFIG.CLICK_DELAY);
    });

    button.setAttribute(CSS_CLASSES.AUTO_CLOSE_BOUND, 'true');
  }

  private bindFormEvents(container: HTMLElement): void {
    const forms = container.querySelectorAll('form');

    forms.forEach((form: HTMLFormElement) => {
      if (form.hasAttribute(CSS_CLASSES.AUTO_CLOSE_BOUND)) return;

      form.addEventListener(BUTTON_TYPES.SUBMIT, () => {
        setTimeout(() => this.onFilterApplied(), EVENT_CONFIG.CLICK_DELAY);
      });

      form.setAttribute(CSS_CLASSES.AUTO_CLOSE_BOUND, 'true');
    });
  }

  private setupDOMObserver(container: HTMLElement): void {
    const observer = new MutationObserver((mutations) => {
      let shouldRecheck = false;

      mutations.forEach((mutation) => {
        if (
          mutation.type === DOM_EVENTS.CHILD_LIST &&
          mutation.addedNodes.length > 0
        ) {
          shouldRecheck = true;
        }
      });

      if (shouldRecheck) {
        setTimeout(() => {
          this.detectAndBindFilterButtons(container);
        }, EVENT_CONFIG.DOM_OBSERVER_DELAY);
      }
    });

    observer.observe(container, {
      childList: true,
      subtree: true,
    });

    this.domObserver = observer;
  }

  ngOnDestroy(): void {
    this.cleanupDOMObserver();
    this.cleanupDynamicComponent();
    this.cleanupEventListeners();
  }

  private cleanupDOMObserver(): void {
    this.domObserver?.disconnect();
    this.domObserver = undefined;
  }

  private cleanupDynamicComponent(): void {
    this.dynamicComponentRef?.destroy();
    this.dynamicComponentRef = undefined;
  }

  private cleanupEventListeners(): void {
    this.boundMouseMove &&
      document.removeEventListener(DOM_EVENTS.MOUSE_MOVE, this.boundMouseMove);
    this.boundMouseUp &&
      document.removeEventListener(DOM_EVENTS.MOUSE_UP, this.boundMouseUp);

    this.boundTouchMove &&
      document.removeEventListener(DOM_EVENTS.TOUCH_MOVE, this.boundTouchMove);
    this.boundTouchEnd &&
      document.removeEventListener(DOM_EVENTS.TOUCH_END, this.boundTouchEnd);

    this.boundMouseMove = undefined;
    this.boundMouseUp = undefined;
    this.boundTouchMove = undefined;
    this.boundTouchEnd = undefined;
  }

  public onBackdropClick(event: Event): void {
    if (event.target === event.currentTarget) {
      this.closeOffcanvas();
    }
  }

  public onFloatingIconMouseDown(event: MouseEvent): void {
    event.preventDefault();
    this.isDragging = true;
    this.hasDragged = false;
    this.dragStartY = event.clientY;
    this.dragStartPosition = this.floatingIconPosition;

    this.boundMouseMove = this.onMouseMove.bind(this);
    this.boundMouseUp = this.onMouseUp.bind(this);

    document.addEventListener(DOM_EVENTS.MOUSE_MOVE, this.boundMouseMove);
    document.addEventListener(DOM_EVENTS.MOUSE_UP, this.boundMouseUp);
  }

  public onFloatingIconTouchStart(event: TouchEvent): void {
    if (event.touches.length === 1) {
      event.preventDefault();
      this.isDragging = true;
      this.hasDragged = false;
      this.dragStartY = event.touches[0].clientY;
      this.dragStartPosition = this.floatingIconPosition;

      this.boundTouchMove = this.onTouchMove.bind(this);
      this.boundTouchEnd = this.onTouchEnd.bind(this);

      document.addEventListener(DOM_EVENTS.TOUCH_MOVE, this.boundTouchMove);
      document.addEventListener(DOM_EVENTS.TOUCH_END, this.boundTouchEnd);
    }
  }

  private onMouseMove(event: MouseEvent): void {
    if (!this.isDragging) return;

    const deltaY = event.clientY - this.dragStartY;
    const windowHeight = window.innerHeight;
    const deltaPercent = (deltaY / windowHeight) * 100;

    if (Math.abs(deltaY) > EVENT_CONFIG.DRAG_THRESHOLD) {
      this.hasDragged = true;
    }

    this.updateIconPosition(deltaPercent);
  }

  private onTouchMove(event: TouchEvent): void {
    if (!this.isDragging || event.touches.length !== 1) return;

    const deltaY = event.touches[0].clientY - this.dragStartY;
    const windowHeight = window.innerHeight;
    const deltaPercent = (deltaY / windowHeight) * 100;

    if (Math.abs(deltaY) > EVENT_CONFIG.DRAG_THRESHOLD) {
      this.hasDragged = true;
    }

    this.updateIconPosition(deltaPercent);
  }

  private updateIconPosition(deltaPercent: number): void {
    const newPosition = this.dragStartPosition + deltaPercent;

    this.floatingIconPosition = Math.max(
      FLOATING_ICON_CONFIG.MIN_POSITION,
      Math.min(FLOATING_ICON_CONFIG.MAX_POSITION, newPosition),
    );
  }

  private onMouseUp(event: MouseEvent): void {
    const wasJustClick = !this.hasDragged;

    this.endDrag();
    if (this.boundMouseMove) {
      document.removeEventListener(DOM_EVENTS.MOUSE_MOVE, this.boundMouseMove);
      this.boundMouseMove = undefined;
    }
    if (this.boundMouseUp) {
      document.removeEventListener(DOM_EVENTS.MOUSE_UP, this.boundMouseUp);
      this.boundMouseUp = undefined;
    }

    if (wasJustClick) {
      setTimeout(() => {
        this.openOffcanvas();
      }, EVENT_CONFIG.CLICK_AFTER_DRAG_DELAY);
    }
  }

  private onTouchEnd(event: TouchEvent): void {
    const wasJustClick = !this.hasDragged;

    this.endDrag();
    if (this.boundTouchMove) {
      document.removeEventListener(DOM_EVENTS.TOUCH_MOVE, this.boundTouchMove);
      this.boundTouchMove = undefined;
    }
    if (this.boundTouchEnd) {
      document.removeEventListener(DOM_EVENTS.TOUCH_END, this.boundTouchEnd);
      this.boundTouchEnd = undefined;
    }

    if (wasJustClick) {
      setTimeout(() => {
        this.openOffcanvas();
      }, EVENT_CONFIG.CLICK_AFTER_DRAG_DELAY);
    }
  }

  private endDrag(): void {
    this.isDragging = false;

    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(
        FLOATING_ICON_CONFIG.STORAGE_KEY,
        this.floatingIconPosition.toString(),
      );
    }
  }

  private loadSavedIconPosition(): void {
    if (typeof localStorage === 'undefined') return;

    const savedPosition = localStorage.getItem(
      FLOATING_ICON_CONFIG.STORAGE_KEY,
    );

    if (!savedPosition) return;

    const position = parseFloat(savedPosition);

    const isValidPosition =
      !isNaN(position) &&
      position >= FLOATING_ICON_CONFIG.MIN_POSITION &&
      position <= FLOATING_ICON_CONFIG.MAX_POSITION;

    this.floatingIconPosition = isValidPosition
      ? position
      : FLOATING_ICON_CONFIG.DEFAULT_POSITION;
  }
}
